import {
  Alert,
  Button,
  Flex,
  Grid,
  Link,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react'
import type { Error } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { Recap } from '@/components/Recap'
import { RecapQuote } from '@/components/Recap/RecapQuote'
import {
  UnsavedFormModal
} from '@/components/UnsavedFormModal'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useMeetingRecord } from '@/context/MeetingRecordProvider'
import { usePartner } from '@/context/PartnerContext'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { useSidebar } from '@/context/SidebarContext'
import type { CommonMeetingRecord } from '@/dtos/CommonMeetingRecord'
import { useGetAllDocuments } from '@/hooks/useDocuments'
import { useCreateContract } from '@/hooks/useWizard'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { FormSection } from '../../../forms/FormSection'
import CommonMeetingRecordForm from '../../vehicle/Step4/forms/CommonMeetingRecordForm'
import { CreateContractForm } from '../../vehicle/Step4/forms/CreateContractForm'
import KapitolCreateContractForm from '../../vehicle/Step4/forms/KapitolCreateContractForm'
import KapitolMeetingRecordForm from '../../vehicle/Step4/forms/KapitolMeetingRecordForm'

export interface CalculationState {
  isCalculated: boolean
  isCalculating: boolean
}

export type Step4FormValues = {
  meetingRecord: CommonMeetingRecord
}

export const Step4 = () => {
  const { currentPartner } = usePartner()
  const { pageSections, setPageSections } = useSidebar()
  const [acceptChangedPrice, setAcceptChangedPrice] = useState(false)
  const [priceChanged, setPriceChanged] = useState(false)
  const [returnBack, setReturnBack] = useState(true)
  const [alerts, setAlerts] = useState<Error[]>([])
  const [shouldSubmit, setShouldSubmit] = useState(false)

  const {
    insuranceParamsId,
    refetchInsuranceParams,
    insuranceParams,
    isDisabled,
  } = useInsuranceParams()
  const { refetch: refetchOffer } = useSelectedQuote()
  const {
    refetch: refetchMR,
    meetingRecord,
    save: saveMeetingRecord,
  } = useMeetingRecord()

  const { refetch: refetchDocuments } = useGetAllDocuments(
    insuranceParamsId,
    insuranceParams!.generalParams.selectedOfferId!,
    { enabled: false },
  )
  const toast = useToast()
  const navigate = useNavigate()

  // sjednani smlouvy
  const { mutateAsync: createContract } = useCreateContract()

  const methods = useForm<Step4FormValues>({
    defaultValues: { meetingRecord },
    // resolver: yupResolver(schema),
    // mode: 'onBlur'
  })

  const {
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = methods

  useEffect(() => {
    if (meetingRecord) reset({ meetingRecord })
  }, [!meetingRecord])

  // zobrazeni validace nabidky ve validation summary (zobrazuje se uz pri getu)
  useEffect(() => {
    if (meetingRecord?.errors)
      meetingRecord.errors.forEach((x) =>
        setError('' as any, { message: x.message }),
      )
  }, [meetingRecord?.errors])

  const isValid = !Object.keys(errors).length

  // handler na click sjednat pojisteni
  const handleSubmitClick = () => {
    setAcceptChangedPrice(priceChanged)
    setShouldSubmit(true)
  }

  // trigger submitu formulare
  useEffect(() => {
    if (shouldSubmit) {
      setShouldSubmit(false)
      submitForm()
    }
  }, [shouldSubmit])

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.TravelInsurance, 4, insuranceParamsId, methods)

  const validationSummaryRef = useRef<HTMLDivElement>(null)
  // v nevalidniho formulare se nascroluje na validationSummary
  const scrollToValidationSummary = () => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  const saveAndCreateContract = (values: Step4FormValues) => {
    return new Promise((resolve, reject) =>
      saveMeetingRecord(values.meetingRecord)
        .then(() => {
          insuranceParamsFormPersistance.clear()

          // sjednani pojistky
          createContract({ paramsId: insuranceParamsId!, acceptChangedPrice })
            .then((createContractResponse) => {
              if (
                createContractResponse.data.priceChanged &&
                !createContractResponse.data.contractCreated
              ) {
                setPriceChanged(true)
                setReturnBack(createContractResponse.data.returnBack)
                if (createContractResponse.data.errors.length > 0) {
                  setAlerts(createContractResponse.data.errors)
                  scrollToValidationSummary()
                  reject(createContractResponse.data.errors)
                }
              } else if (createContractResponse.data.errors.length > 0) {
                setAlerts(createContractResponse.data.errors)
                scrollToValidationSummary()
                reject([])
              }
              else {
                window.scrollTo(0, 0)
                refetchInsuranceParams()
                refetchMR()
                refetchOffer()

                refetchDocuments()

                resolve(createContractResponse)
              }
            })
            .catch((err) => {
              reject(err)
            })
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.status === 400 &&
            err.response.data
          ) {
            // Přidání prefixu meetingRecord, protože je použitý ve formuláři ale data se postují a vracejí bey něj
            Object.keys(err.response.data).forEach((x) => {
              err.response.data['meetingRecord.' + x] = err.response.data[x]
              delete err.response.data[x]
            })
          }

          reject(err)
        })
        .finally(() => { }),
    )
  }

  const submitForm = handleSubmit((values) =>
    saveAndCreateContract(values)
      .then(() => {
        navigate(`/travel/steps/complete/${insuranceParamsId}`)
        // refetch se volá až po vytvoření smlouvy a eventuelním posání emailu
        //refetchInsuranceParams()
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })
      }))

  // registrace sekcí
  useEffect(() => {
    const array: Section[] = []
    let i = 1

    // sekce Rekapitulace pojištění
    array.push({
      id: 'recapitulation',
      sectionTitle: 'Rekapitulace pojištění',
      index: i++,
      children: (
        <CustomWaypoint sectionId="recapitulation">
          <Recap id="recapitulation">
            <RecapQuote />
          </Recap>
        </CustomWaypoint>
      ),
    })

    let partnerMeetingRecordForm = null
    let partnerArrangeContractForm = null
    switch (currentPartner!.partner) {
      case 'Kapitol':
        partnerMeetingRecordForm = <KapitolMeetingRecordForm />
        partnerArrangeContractForm = <KapitolCreateContractForm />
        break
      default:
        partnerMeetingRecordForm = <CommonMeetingRecordForm />
        partnerArrangeContractForm = <CreateContractForm />
        break
    }

    array.push({
      id: 'meetingRecord',
      sectionTitle: 'Záznam z jednání',
      index: i,
      children: (
        <CustomWaypoint sectionId="recapitulation">
          <PageSection id={'meetingRecord'}>
            <FormSection
              number={i++}
              sectionTitle={'Záznam z jednání'}
              wide={false}
            >
              {partnerMeetingRecordForm}
            </FormSection>
          </PageSection>
        </CustomWaypoint>
      ),
    })

    // sekce Sjednání
    array.push({
      id: 'contract',
      sectionTitle: 'Sjednání',
      index: i,
      useFormSection: true,
      children: (
        <CustomWaypoint sectionId="contract">
          <PageSection id={'contract'}>
            <FormSection number={i++} sectionTitle={'Sjednání'} wide={false}>
              <Grid rowGap={12}>
                {!!partnerArrangeContractForm && partnerArrangeContractForm}

                <Flex justifyContent="center" w="100%">
                  <Button
                    colorScheme="green"
                    isDisabled={isSubmitting || !isValid || isDisabled}
                    isLoading={isSubmitting && isValid}
                    loadingText="Sjednávám..."
                    type="button"
                    variant="solid"
                    onClick={handleSubmitClick}
                  >
                    {!priceChanged
                      ? 'Sjednat pojištění'
                      : 'Sjednat pojištění se změnou ceny'}
                  </Button>
                </Flex>
              </Grid>
            </FormSection>
          </PageSection>
        </CustomWaypoint>
      ),
    })

    setPageSections(array)

    return () => {
      setPageSections([])
    }
  }, [isSubmitting, isValid])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Grid gap={8}>
          {pageSections.map((x) => (
            <React.Fragment key={x.id}>{x.children}</React.Fragment>
          ))}

          {(priceChanged || alerts.length > 0) && (
            <Alert colorScheme="red" mb={8} ref={validationSummaryRef}>
              <Stack direction="column">
                {priceChanged && (
                  <Text>
                    Před sjednáním došlo ke změně ceny.
                    {returnBack && (
                      <>
                        {' '}
                        Vraťte se na{' '}
                        <Link
                          as={RouterLink}
                          colorScheme="blue"
                          to={`/travel/steps/2/${insuranceParamsId!}`}
                        >
                          &nbsp;seznam kalkulací&nbsp;
                        </Link>{' '}
                        a vybranou nabídku překalkulujte.
                      </>
                    )}
                  </Text>
                )}

                {alerts.length > 0 &&
                  alerts
                    .map((alert) =>
                      alert.message && alert.message != ''
                        ? alert.message
                        : (alert.specification ?? null),
                    )
                    .filter((x) => x)
                    .map((alert) => (
                      <Text
                        key={alert}
                        dangerouslySetInnerHTML={{ __html: alert! }}
                      />
                    ))}
              </Stack>
            </Alert>
          )}

          <ErrorSummary errors={errors} />
          <UnsavedFormModal {...insuranceParamsFormPersistance} />
        </Grid>
      </form>
    </FormProvider>
  )
}
